// Score saisi prioritaire et fraîcheur des scores FFBB (D-113) :
//  - le bandeau du staff quand le score FFBB diffère du score saisi au club (« Garder le nôtre », « Prendre
//    celui de la FFBB » ou « Corriger le score »), ou quand la FFBB a publié 0-0 sans que rien n'ait été saisi
//    (« Saisir le score ») ; la cloche prévient le staff une fois par nouvelle valeur FFBB ; D-117 : le bandeau
//    nomme le match en entier, et le bureau ne le voit que pour un match à domicile (ou une équipe sans staff) ;
//  - « Récupérer le score » pour tous les membres de l'équipe, sur la page du match et la carte de
//    l'accueil, une fois le match commencé et tant que le résultat n'est pas confirmé : la base dit si une
//    lecture est utile (un appareil par poule toutes les 15 min), le retour est clair.
import { decideScore, ffbbLinksOf, scoreToFetch, type MatchRow, type TeamMatch } from '../lib/api';
import { fetchScore } from '../lib/ffbb-sync';
import { canFetchScore, scoreAlertView } from '../domain/score';
import { localDate, parseLocalDate } from '../domain/schedule';
import { squadName, teamOf, type Ctx } from './context';
import { $, $$, html, mount, toast, type Html } from './dom';
import { time } from './format';
import { fillCrests, oppCrest } from './crest';
import { matchLine } from './match-extras';

/** Le bandeau du staff (rien s'il n'y a rien à trancher). `onEnter` : ouvrir la saisie du score ; `fetchable` :
 *  le 0-0 peut être relu sur la FFBB (« Récupérer le score » dans le bandeau) ; `label` : le match en entier (D-117). */
export function renderScoreAlert(el: HTMLElement, eventId: string, m: MatchRow, onDone: () => void, onEnter: () => void, fetchable = false, label = ''): void {
  const v = scoreAlertView(m);
  if (!v) { mount(el, html``); return; }
  mount(el, html`
    <section class="block mx-alert fsc-alert" role="alert" data-guide="ev-score-alert">
      <span class="eyebrow">${v.eyebrow}</span>
      ${label ? matchLine(label) : ''}
      ${v.kind === 'gap' ? html`
        <div class="fsc-pair">
          <span><small>Saisi au club</small><b>${v.ours}</b></span>
          <span><small>FFBB</small><b>${v.ffbb}</b></span>
        </div>
        <p class="small">${v.text}</p>
        <div class="btn-row">
          <button type="button" class="btn btn-primary btn-sm" id="fsc-keep">Garder le nôtre</button>
          <button type="button" class="btn btn-ghost btn-sm" id="fsc-take">Prendre celui de la FFBB</button>
          <button type="button" class="btn btn-link btn-sm" id="fsc-fix" data-guide="ev-score-fix">Corriger le score</button>
        </div>`
      : html`
        <p><strong>${v.text}</strong></p>
        <p class="small">Rien n’a été saisi au club et la FFBB affiche 0 – 0. Saisis le vrai score : il restera, même si la FFBB ne corrige pas.</p>
        <div class="btn-row"><button type="button" class="btn btn-primary btn-sm" id="fsc-enter">Saisir le score</button></div>
        ${fetchable ? fetchButton(eventId) : ''}`}
    </section>`);
  if (fetchable) wireFetch(el, onDone);
  const decide = async (b: HTMLButtonElement, take: boolean) => {
    b.disabled = true;
    try { await decideScore(eventId, take); toast(take ? 'Score de la FFBB pris' : 'Score du club gardé'); onDone(); }
    catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
  };
  $<HTMLButtonElement>(el, '#fsc-keep')?.addEventListener('click', e => void decide(e.currentTarget as HTMLButtonElement, false));
  $<HTMLButtonElement>(el, '#fsc-take')?.addEventListener('click', e => void decide(e.currentTarget as HTMLButtonElement, true));
  $(el, '#fsc-enter')?.addEventListener('click', onEnter);
  $(el, '#fsc-fix')?.addEventListener('click', onEnter);
}

/** Le bouton et sa réponse (« Score à jour », « Pas encore publié par la FFBB », « Lu il y a 5 min »). */
function fetchButton(eventId: string, cls = ''): Html {
  return html`<div class="fsc-fetch ${cls}">
      <button type="button" class="btn btn-soft btn-sm" data-fetch="${eventId}" data-guide="fetch-score">Récupérer le score</button>
      <span class="small fsc-msg" role="status" aria-live="polite" data-fetch-msg="${eventId}"></span>
    </div>`;
}
/** `onDone` : le score est arrivé (ou a changé) ; la carte de l'accueil garde le score affiché, la page du match se redessine. */
function wireFetch(root: HTMLElement, onDone?: () => void): void {
  for (const b of $$<HTMLButtonElement>(root, '[data-fetch]')) b.addEventListener('click', async () => {
    const id = b.dataset.fetch!;
    const msg = root.querySelector<HTMLElement>(`[data-fetch-msg="${id}"]`);
    b.disabled = true; b.textContent = 'Lecture de la FFBB…';
    const r = await fetchScore(id);
    b.disabled = false; b.textContent = 'Récupérer le score';
    const text = r.done && r.score ? `${r.text} : ${r.score}` : r.text;
    if (msg) msg.textContent = text;
    if (r.done) b.hidden = true;
    if (r.done || r.changed) { toast(text); if (onDone) setTimeout(onDone, 1200); }
  });
}

/** Page du match : tout membre de l'équipe, une fois le match commencé, tant que le résultat n'est pas confirmé. */
export function renderFetchScore(el: HTMLElement, eventId: string, m: MatchRow, start: Date, linked: boolean, onDone: () => void): void {
  if (!linked || !canFetchScore(m, start)) { mount(el, html``); return; }
  mount(el, html`
    <section class="block fsc-fetch-block">
      <p class="fsc-lead"><strong>${m.score_alert === 'zero' ? 'La FFBB affiche 0 – 0' : 'Score pas encore là ?'}</strong>
        <span class="small muted">${m.score_alert === 'zero' ? 'Sûrement une erreur : relire la FFBB, elle a peut-être corrigé.' : 'La FFBB le publie après le match. Une lecture suffit pour toute l’équipe.'}</span></p>
      ${fetchButton(eventId)}
    </section>`);
  wireFetch(el, onDone);
}

/** Accueil : les matchs de mes équipes commencés depuis moins de 48 h, sans résultat confirmé. */
export async function renderScoreFetchCard(el: HTMLElement, ctx: Ctx): Promise<void> {
  const ids = ctx.teams.map(t => t.id);
  if (!ids.length) return;
  const [ms, links] = await Promise.all([scoreToFetch(ids).catch(() => [] as TeamMatch[]), ffbbLinksOf(ids).catch(() => [])]);
  const now = new Date();
  const show = ms.filter(m => links.some(l => l.team_id === m.events.team_id && l.engagement_id === m.ffbb_engagement_id)
    && canFetchScore(m, new Date(m.events.starts_at), now)).slice(0, 2);
  if (!show.length) { mount(el, html``); return; }
  const today = localDate(now);
  mount(el, html`${show.map(m => {
    const day = localDate(new Date(m.events.starts_at));
    const team = teamOf(ctx, m.events.team_id);
    const label = `${squadName(ctx, m.squad_id) ?? team?.name ?? 'Match'} ${m.is_home === false ? 'à' : '–'} ${m.opponent}`;
    return html`
      <section class="card fsc-card" data-guide="home-fetch-score">
        <span class="eyebrow">Score à venir · ${day === today ? 'aujourd’hui' : parseLocalDate(day).toLocaleDateString('fr-FR', { weekday: 'long' })} ${time(new Date(m.events.starts_at))}</span>
        <a class="card-title crest-line" href="#/e/${m.event_id}/${day}">${oppCrest(m, 'opp-crest-sm')}<span>${label}</span></a>
        ${fetchButton(m.event_id)}
      </section>`;
  })}`);
  void fillCrests(el);
  wireFetch(el);
}
